/**
 * PageHeader Component - Standard page header with title, actions, and filters
 * Use this component for consistent layout across all pages
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Badge } from './badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Filter, X, ChevronDown, ChevronUp, RotateCcw, Download, FileText, FileSpreadsheet, Search, FileDown } from 'lucide-react';
import { Switch } from './switch';
import { Input } from './input';
import { Label } from './label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import './PageHeader.css';

/**
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {React.ReactNode} props.icon - Icon component
 * @param {string} props.subtitle - Subtitle/description
 * @param {React.ReactNode} props.actions - Action buttons (right side)
 * @param {Array} props.filters - Filter definitions
 * @param {Object} props.filterValues - Current filter values
 * @param {Function} props.onFilterChange - Filter change callback
 * @param {Function} props.onSearch - Search callback
 * @param {Function} props.onClearFilters - Clear filters callback
 * @param {Function} props.onExportCSV - Export CSV callback
 * @param {Function} props.onExportJSON - Export JSON callback
 * @param {Function} props.onExportPDF - Export PDF callback
 * @param {boolean} props.showExport - Show export button
 * @param {boolean} props.showFilters - Show filters
 */
export function PageHeader({
  title,
  icon: Icon,
  subtitle,
  actions,
  filters = [],
  filterValues = {},
  onFilterChange,
  onSearch,
  onClearFilters,
  onExportCSV,
  onExportJSON,
  onExportPDF,
  showExport = true,
  showFilters = true,
}) {
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const activeFilters = filters.filter(f => {
    const value = filterValues[f.key];
    return value !== undefined && value !== '' && value !== 'all';
  });

  const handleRemoveFilter = (key) => {
    const newValues = { ...filterValues };
    delete newValues[key];
    onFilterChange?.(newValues);
    setTimeout(() => onSearch?.(), 0);
  };

  const getFilterLabel = (filter) => {
    const value = filterValues[filter.key];
    if (filter.type === 'select' && filter.options) {
      const option = filter.options.find(o => o.value === value);
      return option?.label || value;
    }
    return value;
  };

  return (
    <div className="page-header-container">
      {/* Top Row: Title + Actions */}
      <div className="page-header-top">
        <div className="page-header-title-section">
          {Icon && <Icon className="page-header-icon" />}
          <div>
            <h1 className="page-header-title">{title}</h1>
            {subtitle && <p className="page-header-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="page-header-actions">
          {actions}
        </div>
      </div>

      {/* Filter Row: Filter Button + Tags + Export */}
      {(showFilters || showExport) && (
        <div className="page-header-toolbar">
          <div className="page-header-toolbar-left">
            {showFilters && filters.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  data-testid="filter-toggle-btn"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t('common.filters') || 'Filtros'}
                  {activeFilters.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {activeFilters.length}
                    </Badge>
                  )}
                  {isFilterOpen ? (
                    <ChevronUp className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-2" />
                  )}
                </Button>

                {activeFilters.length > 0 && (
                  <>
                    <div className="page-header-filter-tags">
                      {activeFilters.map(filter => (
                        <Badge key={filter.key} variant="secondary" className="filter-tag">
                          <span className="filter-tag-label">{filter.label}:</span>
                          <span className="filter-tag-value">{getFilterLabel(filter)}</span>
                          <button
                            onClick={() => handleRemoveFilter(filter.key)}
                            className="filter-tag-remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClearFilters}>
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Limpar
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          {showExport && (onExportCSV || onExportJSON || onExportPDF) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="export-dropdown-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar
                  <ChevronDown className="h-3 w-3 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onExportPDF && (
                  <DropdownMenuItem onClick={onExportPDF} data-testid="export-pdf-btn">
                    <FileDown className="h-4 w-4 mr-2 text-red-500" />
                    Exportar PDF
                  </DropdownMenuItem>
                )}
                {onExportCSV && (
                  <DropdownMenuItem onClick={onExportCSV} data-testid="export-csv-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Exportar CSV
                  </DropdownMenuItem>
                )}
                {onExportJSON && (
                  <DropdownMenuItem onClick={onExportJSON} data-testid="export-json-btn">
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Exportar JSON
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/* Filter Panel - Expandable */}
      {isFilterOpen && filters.length > 0 && (
        <div className="page-header-filter-panel">
          <div className="page-header-filter-fields">
            {filters.map(filter => (
              <div key={filter.key} className="page-header-filter-field">
                <Label htmlFor={filter.key} className="text-sm">
                  {filter.label}
                </Label>
                {filter.type === 'text' && (
                  <Input
                    id={filter.key}
                    value={filterValues[filter.key] || ''}
                    onChange={(e) => onFilterChange?.({ ...filterValues, [filter.key]: e.target.value })}
                    placeholder={filter.placeholder}
                    className="h-9"
                  />
                )}
                {filter.type === 'date' && (
                  <Input
                    id={filter.key}
                    type="date"
                    value={filterValues[filter.key] || ''}
                    onChange={(e) => onFilterChange?.({ ...filterValues, [filter.key]: e.target.value })}
                    className="h-9"
                  />
                )}
                {filter.type === 'select' && (
                  <Select
                    value={filterValues[filter.key] || 'all'}
                    onValueChange={(value) => onFilterChange?.({ ...filterValues, [filter.key]: value === 'all' ? '' : value })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={filter.placeholder || 'Selecione'} />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options?.map(option => (
                        <SelectItem key={option.value || 'all'} value={option.value || 'all'}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {filter.type === 'checkbox' && (
                  <div className="flex items-center gap-2 h-9">
                    <Switch
                      id={filter.key}
                      checked={!!filterValues[filter.key]}
                      onCheckedChange={(checked) => onFilterChange?.({ ...filterValues, [filter.key]: checked })}
                    />
                    <span className="text-sm text-muted-foreground">
                      {filterValues[filter.key] ? 'Sim' : 'Não'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="page-header-filter-actions">
            <Button onClick={onSearch} data-testid="apply-filters-btn">
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PageHeader;
